import './styles/App.scss';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useLayoutEffect, useState } from 'react';
import moment from 'moment-timezone';
import AdminRoute from './components/admin/adminRoute/AdminRoute';
import AddCountry from './components/admin/pages/countries/AddCountry';
import Countries from './components/admin/pages/countries/Countries';
import EditCountry from './components/admin/pages/countries/EditCountry';
import Login from './components/admin/pages/login/Login';
import { AdminContextProvider } from './components/contexts/adminContext/adminContext';
import { TranslationContextProvider } from './components/contexts/TranslationContext';
import Customers from './components/admin/pages/customers/Customers';
import AddCustomers from './components/admin/pages/customers/AddCustomers';
import OrganizationTypes from './components/admin/pages/organizationTypes/OrganizationTypes';
import AddOrganizationType from './components/admin/pages/organizationTypes/AddOrganizationType';
import EditOrganizationType from './components/admin/pages/organizationTypes/EditOrganizationType';
import { UserContextProvider } from './components/contexts/userContext/userContext';
import Stations from './components/admin/pages/stations/Stations';
import EditStation from './components/admin/pages/stations/EditStation';
import Banners from './components/admin/pages/banners/Banners';
import AddBanner from './components/admin/pages/banners/AddBanner';
import EditBanner from './components/admin/pages/banners/EditBanner';
import ExternalAuth from './components/customer/externalAuth/ExternalAuth';
import AdminTickets from './components/admin/pages/tickets/Tickets';
import InfoContent from './components/admin/pages/infoContent/InfoContent';
import 'react-datepicker/dist/react-datepicker.css';
import AdminBenefits from './components/admin/pages/benefits/AdminBenefits';
import AddBenefit from './components/admin/pages/benefits/AddBenefit';
import EditBenefit from './components/admin/pages/benefits/EditBenefit';
import { ModalsContextProvider } from './components/contexts/modalsContext/modalsContext';
import LayRoutes from './utils/routes/LayRoutes';
import customerRoutes from './utils/routes/customerRoutes';
import RedirectNotFound from './components/customer/pages/redirectNotFound/RedirectNotFound';
import CustomersType from './components/admin/pages/customers/CustomersType';
import EditCustomer from './components/admin/pages/customers/EditCustomer';
import { serverConfigService } from './services/login/Login.api';
import Payments from './components/admin/pages/payments/Payments';
import SocialLinks from './components/admin/pages/socialLinks/SocialLinks';
import AdminUser from './components/admin/pages/users/AdminUser';
import AddAdminUser from './components/admin/pages/users/AddAdminUser';
import EditAdminUser from './components/admin/pages/users/EditAdminUser';
import Renew from './components/admin/pages/renew/Renew';
import PaymentPage from './components/admin/pages/payments/paymentPage/PaymentPage';
import RecoverAdminUser from './components/admin/pages/recoveryUser/RecoverAdminUser';
import SuggestedStations from './components/admin/pages/suggestedStations/SuggestedStations';
import RidePromotions from './components/admin/pages/ridePromotions/RidePromotions';

function App() {
  const token = new URLSearchParams(window.location.search).get('token');
  const [configInfo, setConfigInfo] = useState({
    loading: true,
    config: {
      countryCode: 'GE',
    },
  });

  useLayoutEffect(() => {
    if (token) {
      localStorage.setItem('accessToken', token);
      const afterOAuthRedirectUrl = localStorage.getItem('afterOAuthRedirectUrl');
      if (afterOAuthRedirectUrl) {
        localStorage.removeItem('afterOAuthRedirectUrl');
        window.location.href = afterOAuthRedirectUrl;
      } else {
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        setTimeout(() => {
          window.location.href = url.toString();
        }, 1000);
      }
    }
    const fetchConfig = async () => {
      const res = await serverConfigService();
      setConfigInfo({
        loading: false,
        config: res,
      });
      const html = document.documentElement;
      html.classList.remove('ka-root', 'am-root');
      html.classList.add(res?.countryCode === 'AM' ? 'am-root' : 'ka-root');
    };
    fetchConfig();
  }, []);

  if (configInfo.loading || token) {
    return <div className="h-screen w-screen loading-indicator" />;
  }

  if (moment) {
    moment.tz.setDefault('Asia/Tbilisi');
  }

  return (
    <Router>
      <TranslationContextProvider configInfo={configInfo.config}>
        <ModalsContextProvider>
          <div className="App">
            <AdminContextProvider serverCurrentTime={configInfo.config.currentTime}>
              <UserContextProvider>
                <Routes>
                  <Route path="/admin/login" element={<Login />} />
                  <Route path="/admin/recover-user/:code" element={<RecoverAdminUser />} />
                  <Route
                    exact
                    path="/google/redirect"
                    element={<ExternalAuth type="google" />}
                  />
                  <Route
                    exact
                    path="/google-app/redirect"
                    element={<ExternalAuth type="google-app" />}
                  />
                  <Route
                    exact
                    path="/facebook/redirect"
                    element={<ExternalAuth type="facebook" />}
                  />

                  <Route path="/admin" element={<AdminRoute />}>
                    {/* Country routes */}
                    <Route
                      exact
                      path="/admin/countries"
                      element={<Countries />}
                    />
                    <Route
                      exact
                      path="/admin/countries/add"
                      element={<AddCountry />}
                    />
                    <Route
                      exact
                      path="/admin/countries/:id/edit"
                      element={<EditCountry />}
                    />

                    {/* Customer routes */}

                    <Route
                      exact
                      path="/admin/customers"
                      element={<Customers />}
                    />
                    <Route
                      exact
                      path="/admin/customers/legal"
                      element={<CustomersType type="legal" />}
                    />
                    <Route
                      exact
                      path="/admin/customers/physical"
                      element={<CustomersType />}
                    />
                    <Route
                      exact
                      path="/admin/customers/add"
                      element={<AddCustomers />}
                    />
                    <Route
                      exact
                      path="/admin/customers/:id/edit"
                      element={<EditCustomer />}
                    />

                    {/* Organization type routes */}
                    <Route
                      exact
                      path="/admin/organization-types"
                      element={<OrganizationTypes />}
                    />
                    <Route
                      exact
                      path="/admin/organization-types/add"
                      element={<AddOrganizationType />}
                    />
                    <Route
                      exact
                      path="/admin/organization-types/:id/edit"
                      element={<EditOrganizationType />}
                    />

                    {/* Station routes */}
                    <Route
                      exact
                      path="/admin/stations"
                      element={<Stations />}
                    />
                    <Route
                      exact
                      path="/admin/stations/:id/edit"
                      element={<EditStation />}
                    />
                    <Route
                      exact
                      path="/admin/suggested-stations"
                      element={<SuggestedStations />}
                    />
                    <Route
                      exact
                      path="/admin/ride-promotions"
                      element={<RidePromotions />}
                    />

                    {/* Banners routes */}
                    <Route exact path="/admin/banners" element={<Banners />} />
                    <Route
                      exact
                      path="/admin/banners/add"
                      element={<AddBanner />}
                    />
                    <Route
                      exact
                      path="/admin/banners/:id/edit"
                      element={<EditBanner />}
                    />

                    {/* Tickets route */}
                    <Route
                      exact
                      path="/admin/tickets"
                      element={<AdminTickets />}
                    />

                    {/* Purchase terms route */}
                    <Route
                      exact
                      path="/admin/info-content/:type"
                      element={<InfoContent />}
                    />

                    {/* Benefits route */}
                    <Route
                      exact
                      path="/admin/benefits"
                      element={<AdminBenefits />}
                    />
                    <Route
                      exact
                      path="/admin/benefits/add"
                      element={<AddBenefit />}
                    />
                    <Route
                      exact
                      path="/admin/benefits/:id/edit"
                      element={<EditBenefit />}
                    />

                    {/* PAYMENTS */}
                    <Route
                      exact
                      path="/admin/payments"
                      element={<Payments />}
                    />
                    <Route
                      exact
                      path="/admin/payments/:id"
                      element={<PaymentPage />}
                    />
                    <Route
                      exact
                      path="/admin/social-links"
                      element={<SocialLinks />}
                    />

                    {/* users */}
                    <Route
                      exact
                      path="/admin/users"
                      element={<AdminUser />}
                    />
                    <Route
                      exact
                      path="/admin/users/add"
                      element={<AddAdminUser />}
                    />
                    <Route
                      exact
                      path="/admin/users/:id/edit"
                      element={<EditAdminUser />}
                    />

                    <Route
                      exact
                      path="/admin/renew"
                      element={<Renew />}
                    />
                  </Route>
                  {customerRoutes.routes.map(LayRoutes)}
                  <Route path="*" element={<RedirectNotFound />} />
                </Routes>
              </UserContextProvider>
            </AdminContextProvider>
          </div>
        </ModalsContextProvider>
      </TranslationContextProvider>
    </Router>
  );
}

export default App;
